import type {
  DeviceAdapter,
  EventSourceAdapter,
  ShakeAdapter,
  StorageAdapter,
} from "@wherdkit/core";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Dimensions, Platform } from "react-native";

export const rnStorage: StorageAdapter = {
  getItem: (key) => AsyncStorage.getItem(key),
  setItem: (key, value) => AsyncStorage.setItem(key, value),
  removeItem: (key) => AsyncStorage.removeItem(key),
};

export const rnDevice: DeviceAdapter = {
  getMetadata() {
    const { width, height } = Dimensions.get("window");
    return {
      platform: Platform.OS,
      osVersion: String(Platform.Version),
      screen: { width, height },
    };
  },
};

// Live updates over SSE. React Native has no native EventSource, so this uses
// the optional `react-native-sse` package — if it isn't installed, connect() is
// a no-op and the SDK stays pull-only (foreground + on-open refetch still work).
export const rnEventSource: EventSourceAdapter = {
  connect(url, onSignal) {
    let EventSourceCtor:
      | (new (url: string) => {
          addEventListener: (
            type: string,
            cb: (e: { data?: string | null }) => void,
          ) => void;
          close: () => void;
        })
      | null = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      EventSourceCtor = require("react-native-sse").default;
    } catch {
      return () => {};
    }
    if (!EventSourceCtor) return () => {};

    const es = new EventSourceCtor(url);
    es.addEventListener("message", (e) => {
      try {
        const { type } = JSON.parse(e.data ?? "{}") as { type?: string };
        if (type) onSignal(type);
      } catch {
        // ignore malformed frames
      }
    });
    return () => es.close();
  },
};

interface AccelData {
  x: number;
  y: number;
  z: number;
}

// Total acceleration (g) that counts as a shake when no threshold is configured.
const DEFAULT_SHAKE_THRESHOLD = 1.8;

// Shake detection via expo-sensors. The dependency is optional: if it isn't
// installed, subscribe() is a no-op so the SDK still works without shake.
export const rnShake: ShakeAdapter = {
  subscribe(onShake, options) {
    let Accelerometer: {
      setUpdateInterval: (ms: number) => void;
      addListener: (cb: (d: AccelData) => void) => { remove: () => void };
    } | null = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      Accelerometer = require("expo-sensors").Accelerometer;
    } catch {
      return () => {};
    }
    if (!Accelerometer) return () => {};

    Accelerometer.setUpdateInterval(100);
    const THRESHOLD = options?.threshold ?? DEFAULT_SHAKE_THRESHOLD;
    let lastShake = 0;

    const sub = Accelerometer.addListener(({ x, y, z }) => {
      const magnitude = Math.sqrt(x * x + y * y + z * z);
      const now = Date.now();
      if (magnitude > THRESHOLD && now - lastShake > 1000) {
        lastShake = now;
        onShake();
      }
    });

    return () => sub.remove();
  },
};
